import {chain} from 'lodash';

import {isExistingModifier, ModifiersEnum} from '../../../../srcCommon/inboxes/search/searchModifiersHelpers';
import {ConversationHighlightModel} from '../../../models/api/conversationHighlightModel';
import {SmartMixClassnamesEnum} from '../../../models/api/smartMixMatchModel';
import {InboxesSearchLocationsEnum} from '../../../models/url/models/inboxesUrlModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {CellsContext} from '../../../store/panels/cells/context/cellsContext';
import {
  hasSearchInInboxesParameters,
  InboxesParameters,
  InboxesParametersTypesEnum,
} from '../../../store/panels/inboxes/parameters/inboxesParameters';
import {buildParametersSearchModifiers} from '../../../store/panels/inboxes/parameters/inboxesParametersFetchHelpers';
import {ItemId} from '../cells/content/cellsItemsBase';

interface SearchTrackingOptionsBase {
  queryHash?: string;
}

export function trackSearchSessionStart(trackingOptions: SearchTrackingOptionsBase) {
  const {queryHash} = trackingOptions;

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_SESSION_START, {
    query_hash: queryHash,
  });
}

export function trackSearchSessionEnd(trackingOptions: SearchTrackingOptionsBase) {
  const {queryHash} = trackingOptions;

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_SESSION_END, {
    query_hash: queryHash,
  });
}

export interface SearchQueryTrackingOptions extends SearchTrackingOptionsBase {
  queryText: string;
  queryHash?: string;
  location?: InboxesSearchLocationsEnum;
  isPasted?: boolean;
  isFolderSwitch?: boolean;
}

export function trackSearchQuery(
  trackingOptions: SearchQueryTrackingOptions,
  parameters: InboxesParameters,
  cellsContext?: CellsContext,
) {
  const {queryText, queryHash, location, isPasted = false, isFolderSwitch = false} = trackingOptions;

  const tokens = queryText.split(/\s/);

  const usedModifiers = chain(tokens)
    .map((token) => {
      if (!token.includes(':')) {
        return undefined;
      }

      const [modifier, value] = token.split(':');
      const lowercaseModifier = modifier.toLowerCase();

      // We must check if modifier is a valid modifier, otherwise we will track tokens such as `https://...`.
      // We must check if value is defined, otherwise we will track tokens with the shape `modifier:`.
      if (!isExistingModifier(lowercaseModifier) || !value) {
        return undefined;
      }

      return lowercaseModifier;
    })
    .compact()
    .uniq()
    .value();

  const addedModifiers =
    parameters.type !== InboxesParametersTypesEnum.TEAMMATE_MESSAGES
      ? buildParametersSearchModifiers(parameters, cellsContext)
      : '';

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_QUERY, {
    query_hash: queryHash,
    query: queryText,
    full_query: addedModifiers.length > 0 ? `${addedModifiers} ${queryText}` : queryText,
    tokens_count: tokens.length,
    used_modifiers: usedModifiers.join(','),
    added_modifiers: addedModifiers.split(' ').join(','),
    location: location || (hasSearchInInboxesParameters(parameters) ? parameters.search.location : undefined),
    pasted_search_query: isPasted,
    folder_switch: isFolderSwitch,
  });
}

export function trackSearchClearQuery(trackingOptions: SearchTrackingOptionsBase) {
  const {queryHash} = trackingOptions;

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_CLEAR_QUERY, {
    query_hash: queryHash,
  });
}

interface SearchDropdownClickTrackingOptions extends SearchTrackingOptionsBase {
  isRecentQuery: boolean;
  isSmartMixItem: boolean;
  isModifierSuggestion: boolean;
  modifierSuggestionType: ModifiersEnum | null;
  isConversationSuggestion: boolean;
  conversationClassificationLabels?: ReadonlyArray<string>;
  modifierHelperType: ModifiersEnum | null;
  selectedWithTab: boolean;
  smartMixType: SmartMixClassnamesEnum | null;
}

export function trackSearchDropdownClick(trackingOptions: SearchDropdownClickTrackingOptions) {
  const {
    queryHash,
    isRecentQuery,
    isSmartMixItem,
    isModifierSuggestion,
    modifierSuggestionType,
    isConversationSuggestion,
    conversationClassificationLabels,
    modifierHelperType,
    selectedWithTab,
    smartMixType,
  } = trackingOptions;

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_DROPDOWN_CLICK, {
    query_hash: queryHash,
    is_recent_query: isRecentQuery,
    is_smart_mix: isSmartMixItem,
    is_modifier_suggestion: isModifierSuggestion,
    modifier_suggestion_type: modifierSuggestionType,
    is_conversation_suggestion: isConversationSuggestion,
    conversation_classification_labels: conversationClassificationLabels,
    modifier_helper_type: modifierHelperType,
    selected_with_tab: selectedWithTab,
    smart_mix_type: smartMixType,
  });
}

interface SearchDropdownExpandTrackingOptions extends SearchTrackingOptionsBase {
  expand: boolean;
}

export function trackSearchDropdownExpand(trackingOptions: SearchDropdownExpandTrackingOptions) {
  const {queryHash, expand} = trackingOptions;

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_DROPDOWN_EXPAND, {
    query_hash: queryHash,
    expand,
  });
}

interface SearcHintClickTrackingOptions extends SearchTrackingOptionsBase {
  type: string;
  shouldEnrichQuery?: boolean;
}

export function trackSearchHintClick(trackingOptions: SearcHintClickTrackingOptions) {
  const {type, shouldEnrichQuery, queryHash} = trackingOptions;

  // Track hint click.
  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_HINT_CLICK, {
    type,
    enrich_query: shouldEnrichQuery,
    query_hash: queryHash,
  });
}

export function trackSearchHintMoreFiltersClick(trackingOptions: SearchTrackingOptionsBase) {
  const {queryHash} = trackingOptions;

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_HINT_MORE_FILTERS_CLICK, {
    query_hash: queryHash,
  });
}

export function trackSearchQueryBuilderClick(trackingOptions: SearchTrackingOptionsBase) {
  const {queryHash} = trackingOptions;

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_QUERY_BUILDER_CLICK, {
    query_hash: queryHash,
  });
}

export function trackSearchQueryBuilderResetClick(trackingOptions: SearchTrackingOptionsBase) {
  const {queryHash} = trackingOptions;

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_QUERY_BUILDER_RESET_CLICK, {
    query_hash: queryHash,
  });
}

interface SearchQueryBuilderSearchClickTrackingOptions extends SearchTrackingOptionsBase {
  changedInputs: ReadonlyArray<string>;
}

export function trackSearchQueryBuilderSearchClick(
  trackingOptions: SearchQueryBuilderSearchClickTrackingOptions,
) {
  const {changedInputs, queryHash} = trackingOptions;

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_QUERY_BUILDER_SEARCH_CLICK, {
    query_hash: queryHash,
    changed_inputs: changedInputs,
  });
}

interface SearchConversationCellClickOptions<T extends ItemId> extends SearchTrackingOptionsBase {
  id: T;
  position: number;
  version?: string;
  matchedQueries: ReadonlyArray<string> | undefined;
  highlights: ConversationHighlightModel | undefined;
  highlightLength: number;
  classificationLabels: ReadonlyArray<string> | undefined;
}

export function trackSearchConversationCellClick<T extends ItemId = ItemId>(
  trackingOptions: SearchConversationCellClickOptions<T>,
) {
  const {
    id,
    position,
    matchedQueries = [],
    highlights,
    highlightLength,
    classificationLabels,
    queryHash,
  } = trackingOptions;

  const computeHighlightKeyTracking = (key: 'recipient' | 'summary' | 'subject' | 'attachments') => {
    const highlightKey = highlights?.[key];

    if (!highlightKey) {
      return {
        [`${key}_highlights`]: 0,
        [`${key}_acceptable_highlights`]: 0,
      };
    }

    return {
      [`${key}_highlights`]: highlightKey.ranges.length,
      [`${key}_acceptable_highlights`]: highlightKey.ranges.filter((range) => range.start < highlightLength)
        .length,
    };
  };

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_P2_CONVERSATION_CLICK, {
    query_hash: queryHash,
    selected_conversation_id: id,
    position,
    matched_queries: matchedQueries.join(','),
    ...computeHighlightKeyTracking('recipient'),
    ...computeHighlightKeyTracking('summary'),
    ...computeHighlightKeyTracking('subject'),
    ...computeHighlightKeyTracking('attachments'),
    classification_labels: classificationLabels?.join(','),
  });
}

export function trackSearchNoResultsHelpfulTipClick(trackingOptions: SearchTrackingOptionsBase) {
  const {queryHash} = trackingOptions;

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_NO_RESULTS_HELPFUL_TIP_CLICK, {
    query_hash: queryHash,
  });
}

interface SearchNoResultsNudgeTrackingOptions extends SearchTrackingOptionsBase {
  currentLocation: InboxesSearchLocationsEnum;
  suggestedLocation: InboxesSearchLocationsEnum;
}

export function trackSearchNoResultsNudgeClick(trackingOptions: SearchNoResultsNudgeTrackingOptions) {
  const {queryHash, currentLocation, suggestedLocation} = trackingOptions;

  trackInterfaceEvent(InterfaceEventsEnum.SEARCH_NO_RESULTS_NUDGE_CLICK, {
    query_hash: queryHash,
    location: currentLocation,
    suggested_location: suggestedLocation,
  });
}
